import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Button, CircularProgress, Container,
  Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Paper, Stack, TextField, Typography,
} from "@mui/material";
import { MdAdd, MdDelete, MdEdit, MdRefresh, MdSave } from "react-icons/md";
import { db } from "../../firebase/firebase-config";
import Colors from "../../assets/styles/Colors";

const DOC_REF = () => db.collection("home_config").doc("top_services");

async function fetchServices() {
  const snap = await DOC_REF().get();
  return snap.exists ? (snap.data().services || []) : [];
}

async function persistServices(services) {
  await DOC_REF().set({ services, updatedAt: new Date().toISOString() }, { merge: true });
}

// ── ServiceForm ────────────────────────────────────────────────────────────────

function ServiceForm({ open, onClose, initial, onSave }) {
  const blank = { name: "", subtitle: "", emoji: "", clickMe: "" };
  const [form, setForm]   = useState(blank);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setForm(initial ? { ...blank, ...initial } : blank);
      setError("");
    }
  }, [open, initial]); // eslint-disable-line react-hooks/exhaustive-deps

  const handle = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSave = () => {
    if (!form.name.trim()) { setError("El nombre es obligatorio."); return; }
    onSave(form);
    onClose();
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: Colors.bgTertiary,
      color: Colors.textPrimary,
      "& fieldset": { borderColor: Colors.borderSecondary },
      "&:hover fieldset": { borderColor: Colors.borderPrimary },
    },
    "& .MuiInputLabel-root": { color: Colors.textSecondary },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { backgroundColor: Colors.bgSecondary, border: `1px solid ${Colors.borderSecondary}` } }}
    >
      <DialogTitle sx={{ color: Colors.textPrimary }}>{initial ? "Editar servicio" : "Nuevo servicio"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField label="Nombre *" value={form.name} onChange={handle("name")} fullWidth size="small" sx={inputSx} />
          <TextField label="Subtítulo" value={form.subtitle} onChange={handle("subtitle")} fullWidth size="small" sx={inputSx} />
          <TextField label="Emoji / Ícono" value={form.emoji} onChange={handle("emoji")} fullWidth size="small" placeholder="🚀" sx={inputSx} />
          <TextField label="URL de destino" value={form.clickMe} onChange={handle("clickMe")} fullWidth size="small" placeholder="https://..." sx={inputSx} />
          {error && <Typography variant="caption" sx={{ color: Colors.error }}>{error}</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ color: Colors.textSecondary, textTransform: "none" }}>Cancelar</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          startIcon={<MdSave />}
          sx={{ backgroundColor: Colors.primary, color: Colors.white, textTransform: "none", borderRadius: 2 }}
        >
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── TopServiciosView ───────────────────────────────────────────────────────────

export default function TopServiciosView() {
  const [services,   setServices]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [saving,     setSaving]     = useState(false);
  const [saveStatus, setSaveStatus] = useState(""); // "saved" | "error" | ""
  const [addOpen,    setAddOpen]    = useState(false);
  const [editIdx,    setEditIdx]    = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try { setServices(await fetchServices()); } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleSave = async () => {
    setSaving(true);
    setSaveStatus("");
    try {
      await persistServices(services);
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus(""), 3000);
    } catch {
      setSaveStatus("error");
    } finally {
      setSaving(false);
    }
  };

  const addService = (data) => {
    setServices((prev) => [...prev, data]);
  };

  const updateService = (idx, data) => {
    setServices((prev) => prev.map((s, i) => (i === idx ? data : s)));
    setEditIdx(null);
  };

  const removeService = (idx) => {
    setServices((prev) => prev.filter((_, i) => i !== idx));
    setConfirmDel(null);
  };

  return (
    <Box sx={{ width: "100%", py: 3 }}>
      <Container maxWidth="xl">

        {/* Header */}
        <Paper sx={{ mb: 3, p: 2.5, borderRadius: 3, background: `linear-gradient(135deg, ${Colors.bgSecondary} 0%, ${Colors.bgTertiary} 100%)`, border: `1px solid ${Colors.borderSecondary}` }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h5" sx={{ color: Colors.textPrimary }}>Top Servicios Activación</Typography>
              <Typography variant="body2" sx={{ color: Colors.textSecondary }}>
                Tarjetas de servicios destacados en la pantalla de inicio (sección 5)
              </Typography>
            </Box>
            <Stack direction="row" spacing={1.5}>
              <IconButton onClick={refresh} sx={{ color: Colors.textSecondary }}><MdRefresh size={20} /></IconButton>
              <Button
                variant="outlined"
                startIcon={<MdAdd />}
                onClick={() => setAddOpen(true)}
                sx={{ borderColor: Colors.borderPrimary, color: Colors.textPrimary, textTransform: "none", borderRadius: 2 }}
              >
                Agregar
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={16} sx={{ color: Colors.white }} /> : <MdSave />}
                sx={{ backgroundColor: Colors.primary, color: Colors.white, textTransform: "none", borderRadius: 2 }}
              >
                {saving ? "Guardando..." : "Guardar cambios"}
              </Button>
            </Stack>
          </Stack>

          {saveStatus === "saved" && (
            <Typography variant="caption" sx={{ color: Colors.success, mt: 1, display: "block" }}>
              Servicios guardados correctamente.
            </Typography>
          )}
          {saveStatus === "error" && (
            <Typography variant="caption" sx={{ color: Colors.error, mt: 1, display: "block" }}>
              Error al guardar. Intenta de nuevo.
            </Typography>
          )}
        </Paper>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress sx={{ color: Colors.primary }} />
          </Box>
        )}

        {!loading && (
          <Paper sx={{ borderRadius: 3, backgroundColor: Colors.bgSecondary, border: `1px solid ${Colors.borderSecondary}`, overflow: "hidden" }}>
            {/* Table header */}
            <Box sx={{ px: 3, py: 1.5, backgroundColor: Colors.bgTertiary, borderBottom: `1px solid ${Colors.borderSecondary}` }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box sx={{ width: 48, flexShrink: 0 }}><Typography variant="subtitle2" sx={{ color: Colors.textSecondary }}>Ícono</Typography></Box>
                <Box sx={{ flex: 2 }}><Typography variant="subtitle2" sx={{ color: Colors.textSecondary }}>Nombre</Typography></Box>
                <Box sx={{ flex: 3 }}><Typography variant="subtitle2" sx={{ color: Colors.textSecondary }}>Subtítulo</Typography></Box>
                <Box sx={{ flex: 2 }}><Typography variant="subtitle2" sx={{ color: Colors.textSecondary }}>URL</Typography></Box>
                <Box sx={{ width: 90, flexShrink: 0 }}><Typography variant="subtitle2" sx={{ color: Colors.textSecondary }}>Acciones</Typography></Box>
              </Stack>
            </Box>

            {services.length === 0 && (
              <Box sx={{ px: 3, py: 4, textAlign: "center" }}>
                <Typography sx={{ color: Colors.textSecondary }}>Sin servicios aún. Esta sección aparecerá cuando agregues el primero.</Typography>
              </Box>
            )}

            {services.map((svc, idx) => (
              <Box
                key={idx}
                sx={{
                  px: 3, py: 1.75,
                  borderBottom: `1px solid ${Colors.borderSecondary}`,
                  "&:hover": { backgroundColor: Colors.bgTertiary },
                  transition: "background-color 0.15s",
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box sx={{ width: 48, flexShrink: 0, textAlign: "center", fontSize: 22 }}>
                    {svc.emoji || "—"}
                  </Box>
                  <Box sx={{ flex: 2 }}>
                    <Typography variant="body2" sx={{ color: Colors.textPrimary, fontWeight: 600 }}>{svc.name || "—"}</Typography>
                  </Box>
                  <Box sx={{ flex: 3, overflow: "hidden" }}>
                    <Typography variant="caption" sx={{ color: Colors.textSecondary, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {svc.subtitle || "—"}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 2, overflow: "hidden" }}>
                    <Typography variant="caption" sx={{ color: Colors.link, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {svc.clickMe || "—"}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0.5} sx={{ width: 90, flexShrink: 0 }}>
                    <IconButton size="small" onClick={() => setEditIdx(idx)} sx={{ color: Colors.textSecondary }} title="Editar">
                      <MdEdit size={16} />
                    </IconButton>
                    <IconButton size="small" onClick={() => setConfirmDel(idx)} sx={{ color: Colors.error }} title="Eliminar">
                      <MdDelete size={16} />
                    </IconButton>
                  </Stack>
                </Stack>
              </Box>
            ))}
          </Paper>
        )}

        {/* Add */}
        <ServiceForm open={addOpen} onClose={() => setAddOpen(false)} initial={null} onSave={addService} />

        {/* Edit */}
        {editIdx !== null && (
          <ServiceForm
            open={editIdx !== null}
            onClose={() => setEditIdx(null)}
            initial={services[editIdx]}
            onSave={(data) => updateService(editIdx, data)}
          />
        )}

        {/* Confirm delete */}
        <Dialog
          open={confirmDel !== null}
          onClose={() => setConfirmDel(null)}
          PaperProps={{ sx: { backgroundColor: Colors.bgSecondary, border: `1px solid ${Colors.borderSecondary}` } }}
        >
          <DialogTitle sx={{ color: Colors.textPrimary }}>Confirmar eliminación</DialogTitle>
          <DialogContent>
            <Typography sx={{ color: Colors.textSecondary }}>
              ¿Eliminar "{confirmDel !== null ? services[confirmDel]?.name : ""}"?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDel(null)} sx={{ color: Colors.textSecondary }}>Cancelar</Button>
            <Button onClick={() => removeService(confirmDel)} sx={{ color: Colors.error }}>Eliminar</Button>
          </DialogActions>
        </Dialog>

      </Container>
    </Box>
  );
}
